import React, { useState, useEffect } from "react";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "./AppStyles.css";
import logo from "./logo.svg"; // Import the logo
import { doc, getDoc } from "firebase/firestore";
import {
  db,
  markUserAs2FARequired,
  clearUserFrom2FARequired,
} from "./firebase";
import {
  isGlobalTwoFactorRequired,
} from "./enforceTwoFactor";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Clear any error messages when email or password changes
    if (error) setError(null);
  }, [email, password, error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoginSuccess("");
    setIsLoading(true);

    // CRITICAL: Signal that authentication processing is starting IMMEDIATELY
    // This must happen BEFORE any Firebase auth calls to prevent navigation flashes
    const authStartEvent = new CustomEvent('auth-processing-start');
    window.dispatchEvent(authStartEvent);
    console.log("🔍 Auth processing started - global overlay should now be visible");

    const auth = getAuth();
    try {
      // First step: authenticate with email and password
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      setLoginSuccess("Authentication successful. Checking 2FA status...");

      // Check if 2FA is enabled for this user BEFORE setting any state
      const userRef = doc(db, "users", user.uid);
      console.log("🔍 Reading user document for 2FA check:", user.uid);
      
      const userDoc = await getDoc(userRef);
      console.log("🔍 User document exists:", userDoc.exists());
      
      if (userDoc.exists()) {
        console.log("🔍 User document data:", userDoc.data());
        console.log("🔍 twoFactorEnabled:", userDoc.data().twoFactorEnabled);
      }

      // Check if 2FA is globally required
      let twoFactorRequired = false;
      try {
        twoFactorRequired = await isGlobalTwoFactorRequired();
        console.log("🔍 Global 2FA required:", twoFactorRequired);
      } catch (error) {
        console.log("🔍 Could not check global 2FA settings, defaulting to enforced:", error);
        // If we can't check the global settings, default to requiring 2FA for security
        twoFactorRequired = true;
      }

      // CRITICAL: Mark user for 2FA FIRST, before any auth state changes
      let requires2FA = false;

      // User has 2FA enabled - require verification
      if (userDoc.exists() && userDoc.data().twoFactorEnabled) {
        console.log("2FA is enabled for this user, will show verification screen");
        requires2FA = true;
      }
      // 2FA is required globally but not set up for this user yet
      else if (
        twoFactorRequired &&
        (!userDoc.exists() || !userDoc.data().twoFactorEnabled)
      ) {
        console.log("2FA is required but not set up, will force setup");
        requires2FA = true;
      } else if (twoFactorRequired) {
        // Global 2FA is required, enforce it
        console.log("🔍 Global 2FA is enforced, requiring 2FA");
        requires2FA = true;
      } else {
        console.log("🔍 2FA is not required for this user");
        requires2FA = false;
      }

      if (requires2FA) {
        // Mark this user as requiring 2FA IMMEDIATELY (before App.js can react)
        console.log("🔍 Marking user for 2FA and letting App.js handle the flow");
        markUserAs2FARequired(user.uid);
        
        // Store pending user info for App.js
        localStorage.setItem("2fa_pending_email", user.email);
        localStorage.setItem("2fa_pending_userid", user.uid);
        
        // Don't show Login's own 2FA screens - let App.js handle it
        // The marking will trigger App.js to show the 2FA screen
        
        console.log("User marked for 2FA, App.js will handle the 2FA flow");
        
        // CRITICAL FIX: Dispatch custom event immediately (no delay)
        // This ensures App.js sees the marking we just set
        // Pass the 2FA status to avoid another database lookup in App.js
        const has2FAEnabled = userDoc.exists() && userDoc.data().twoFactorEnabled;
        const event = new CustomEvent('2fa-marked', { 
          detail: { 
            userId: user.uid, 
            email: user.email,
            has2FAEnabled: has2FAEnabled,
            needsSetup: !has2FAEnabled  // If they don't have 2FA enabled, they need setup
          } 
        });
        window.dispatchEvent(event);
        
        // The auth state change will trigger App.js to show 2FA screen
        return;
      } else {
        // No 2FA required - proceed normally
        console.log("2FA is not enabled or required, proceeding to home page");

        // Signal that auth processing is complete (no 2FA needed)
        const authCompleteEvent = new CustomEvent('auth-processing-complete');
        window.dispatchEvent(authCompleteEvent);

        // Clear any previous 2FA requirements for this user
        clearUserFrom2FARequired(user.uid);

        navigate("/home");
      }
    } catch (error) {
      console.error("Login error:", error);
      
      // Clear the auth processing overlay on error
      const authCompleteEvent = new CustomEvent('auth-processing-complete');
      window.dispatchEvent(authCompleteEvent);
      
      setError(
        error.message.includes("auth/")
          ? "Invalid email or password. Please try again."
          : error.message
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <img src={logo} alt="Logo" className="login-logo" />

        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Processing...</p>
          </div>
        )}

        {loginSuccess && <p className="success-message">{loginSuccess}</p>}

        <h2>Login</h2>
        {error && <p className="error-message">{error}</p>}
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;
